import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calculator, RotateCcw, ArrowRight, Layers, Grid3X3, Droplets, PaintBucket, Ruler } from 'lucide-react';
import { Link } from 'react-router-dom';
import { trackPageView } from '../utils/analytics';
import SEOHead from '../components/SEOHead';

const projectTypes = [
  { id: 'room', label: 'Room / House', icon: Layers },
  { id: 'wall', label: 'Boundary Wall', icon: Grid3X3 },
  { id: 'slab', label: 'Slab / Foundation', icon: Layers },
  { id: 'tiling', label: 'Tiling', icon: PaintBucket },
  { id: 'plastering', label: 'Plastering', icon: Droplets },
];

const cementGrades = [
  { id: 'lion-classic', label: 'Lion Classic' },
  { id: 'lion-supreme', label: 'Lion Supreme' },
  { id: 'hathi-cement', label: 'Hathi Cement' },
  { id: 'siddhee-cement', label: 'Siddhee Cement' },
];

const unitSystems = [
  { id: 'meters', label: 'Meters', abbr: 'm' },
  { id: 'feet', label: 'Feet', abbr: 'ft' },
  { id: 'inches', label: 'Inches', abbr: 'in' },
];

const toMeters = (value, unit) => {
  const v = parseFloat(value) || 0;
  if (unit === 'feet') return v * 0.3048;
  if (unit === 'inches') return v * 0.0254;
  return v;
};

const CalculatorPage = () => {
  const [projectType, setProjectType] = useState('room');
  const [dimensions, setDimensions] = useState({ length: '', width: '', height: '', thickness: '' });
  const [unit, setUnit] = useState('feet');
  const [cementGrade, setCementGrade] = useState('lion-classic');
  const [results, setResults] = useState(null);

  useEffect(() => { trackPageView('/calculator', 'Project Calculator'); }, []);

  const handleDimensionChange = (field, value) => {
    setDimensions(prev => ({ ...prev, [field]: value }));
  };

  const calculate = () => {
    const l = toMeters(dimensions.length, unit);
    const w = toMeters(dimensions.width, unit);
    const h = toMeters(dimensions.height, unit);
    const t = toMeters(dimensions.thickness, unit);

    if (!l || (!w && projectType !== 'wall')) return;

    let cementBags = 0, sand = 0, aggregate = 0, blocks = 0, tileAdhesive = 0, plaster = 0;
    let description = '';
    const abbr = unitSystems.find(u => u.id === unit).abbr;

    switch (projectType) {
      case 'room': {
        const area = l * w;
        const hVal = h || 3;
        const slabVolume = area * 0.15;
        const wallLength = 2 * (l + w);
        const wallVolume = wallLength * hVal * 0.23;
        const totalVol = slabVolume + wallVolume * 0.3;
        cementBags = Math.ceil(totalVol * 8.5);
        sand = +(totalVol * 0.45).toFixed(2);
        aggregate = +(totalVol * 0.9).toFixed(2);
        blocks = Math.ceil((wallLength * hVal) / (0.6 * 0.2));
        plaster = Math.ceil(wallLength * hVal * 2 * 1.2);
        tileAdhesive = Math.ceil(area * 5);
        description = `Room: ${dimensions.length}${abbr} x ${dimensions.width}${abbr} x ${dimensions.height || (unit === 'meters' ? '3' : unit === 'feet' ? '10' : '120')}${abbr}`;
        break;
      }
      case 'wall': {
        const hVal = h || 1.5;
        const tVal = t || 0.23;
        const wallArea = l * hVal;
        const wallVol = wallArea * tVal;
        cementBags = Math.ceil(wallVol * 8);
        sand = +(wallVol * 0.45).toFixed(2);
        blocks = Math.ceil(wallArea / (0.6 * 0.2));
        description = `Wall: ${dimensions.length}${abbr} long x ${dimensions.height || '-'}${abbr} high`;
        break;
      }
      case 'slab': {
        const tVal = t || 0.15;
        const slabVol = l * w * tVal;
        cementBags = Math.ceil(slabVol * 8.5);
        sand = +(slabVol * 0.45).toFixed(2);
        aggregate = +(slabVol * 0.9).toFixed(2);
        description = `Slab: ${dimensions.length}${abbr} x ${dimensions.width}${abbr} x ${dimensions.thickness || '-'}${abbr}`;
        break;
      }
      case 'tiling': {
        const tileArea = l * w;
        tileAdhesive = Math.ceil(tileArea * 5);
        description = `Tiling: ${dimensions.length}${abbr} x ${dimensions.width}${abbr} = ${tileArea.toFixed(1)} sq.m`;
        break;
      }
      case 'plastering': {
        const plasterArea = l * w;
        plaster = Math.ceil(plasterArea * 1.2);
        cementBags = Math.ceil(plasterArea * 0.15);
        sand = +(plasterArea * 0.015).toFixed(2);
        description = `Plastering: ${dimensions.length}${abbr} x ${dimensions.width}${abbr} = ${plasterArea.toFixed(1)} sq.m`;
        break;
      }
      default: break;
    }

    setResults({ cementBags, sand, aggregate, blocks, tileAdhesive, plaster, description });
  };

  const reset = () => {
    setDimensions({ length: '', width: '', height: '', thickness: '' });
    setResults(null);
  };

  const showHeight = ['room', 'wall'].includes(projectType);
  const showThickness = ['wall', 'slab'].includes(projectType);
  const showWidth = projectType !== 'wall';
  const abbr = unitSystems.find(u => u.id === unit).abbr;

  return (
    <div className="min-h-screen bg-void" data-testid="calculator-page">
      <SEOHead
        title="Project Calculator"
        description="Free online cement calculator — estimate exact bags needed for your construction project. Supports residential, commercial, and infrastructure projects."
        path="/calculator"
      />
      {/* Hero */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-20" />
        <motion.div className="absolute top-1/2 right-0 w-[600px] h-[600px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 60%)' }} />
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          {/* Step indicator */}
          <div className="flex items-center gap-3 mb-6">
            <Link to="/tools" className="text-gray-500 hover:text-white text-xs font-mono transition-colors">Smart Tools</Link>
            <span className="text-gray-700">/</span>
            <Link to="/strength-scanner" className="text-gray-500 hover:text-coral text-xs font-mono transition-colors">Step 1</Link>
            <span className="text-gray-700">/</span>
            <Link to="/savings-calculator" className="text-gray-500 hover:text-mint text-xs font-mono transition-colors">Step 2</Link>
            <span className="text-gray-700">/</span>
            <span className="text-blue-400 text-xs font-mono">Step 3</span>
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <span className="text-blue-400 font-mono text-sm mb-4 block">STEP 3 — PROJECT CALCULATOR</span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Project<br /><span className="text-gradient">Calculator</span>
            </h1>
            <p className="text-gray-400 text-lg">Estimate the materials you need for your construction project. Get accurate quantities for cement, blocks, adhesives and more.</p>
          </motion.div>
        </div>
      </section>

      {/* Calculator */}
      <section className="pb-20 md:pb-32">
        <div className="container mx-auto px-4 md:px-8 max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Input Panel */}
            <div className="lg:col-span-3">
              <div className="bg-surface border border-white/5 rounded-2xl p-6 md:p-8 space-y-8">
                {/* Project Type */}
                <div>
                  <label className="font-display text-sm font-medium text-white mb-4 block">Project Type</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {projectTypes.map((type) => (
                      <button key={type.id} onClick={() => { setProjectType(type.id); setResults(null); }} data-testid={`calc-type-${type.id}`}
                        className={`p-4 rounded-xl text-left transition-all ${projectType === type.id ? 'bg-coral text-white border border-coral' : 'bg-void border border-white/10 text-gray-300 hover:border-white/30'}`}>
                        <type.icon className="w-5 h-5 mb-2" />
                        <span className="text-sm font-display font-medium">{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Unit Toggle */}
                <div>
                  <label className="font-display text-sm font-medium text-white mb-3 block flex items-center gap-2">
                    <Ruler className="w-4 h-4 text-coral" /> Measurement Unit
                  </label>
                  <div className="flex gap-2">
                    {unitSystems.map((u) => (
                      <button key={u.id} onClick={() => { setUnit(u.id); setResults(null); }} data-testid={`calc-unit-${u.id}`}
                        className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-display font-medium transition-all ${unit === u.id ? 'bg-avi-gold/20 text-avi-gold border border-avi-gold/30' : 'bg-void border border-white/10 text-gray-400 hover:border-white/20'}`}>
                        {u.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Dimensions */}
                <div>
                  <label className="font-display text-sm font-medium text-white mb-4 block">Dimensions ({abbr})</label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-gray-500 text-xs mb-1 block">Length ({abbr})</label>
                      <input type="number" value={dimensions.length} onChange={(e) => handleDimensionChange('length', e.target.value)}
                        placeholder={unit === 'feet' ? 'e.g. 30' : unit === 'inches' ? 'e.g. 360' : 'e.g. 10'} data-testid="calc-input-length"
                        className="w-full bg-void border border-white/10 focus:border-coral rounded-xl px-4 py-3 text-white placeholder:text-gray-600 outline-none" />
                    </div>
                    {showWidth && (
                      <div>
                        <label className="text-gray-500 text-xs mb-1 block">Width ({abbr})</label>
                        <input type="number" value={dimensions.width} onChange={(e) => handleDimensionChange('width', e.target.value)}
                          placeholder={unit === 'feet' ? 'e.g. 25' : unit === 'inches' ? 'e.g. 300' : 'e.g. 8'} data-testid="calc-input-width"
                          className="w-full bg-void border border-white/10 focus:border-coral rounded-xl px-4 py-3 text-white placeholder:text-gray-600 outline-none" />
                      </div>
                    )}
                    {showHeight && (
                      <div>
                        <label className="text-gray-500 text-xs mb-1 block">Height ({abbr})</label>
                        <input type="number" value={dimensions.height} onChange={(e) => handleDimensionChange('height', e.target.value)}
                          placeholder={unit === 'feet' ? 'e.g. 10' : unit === 'inches' ? 'e.g. 120' : 'e.g. 3'} data-testid="calc-input-height"
                          className="w-full bg-void border border-white/10 focus:border-coral rounded-xl px-4 py-3 text-white placeholder:text-gray-600 outline-none" />
                      </div>
                    )}
                    {showThickness && (
                      <div>
                        <label className="text-gray-500 text-xs mb-1 block">Thickness ({abbr})</label>
                        <input type="number" value={dimensions.thickness} onChange={(e) => handleDimensionChange('thickness', e.target.value)}
                          placeholder={unit === 'feet' ? 'e.g. 0.5' : unit === 'inches' ? 'e.g. 6' : 'e.g. 0.15'} data-testid="calc-input-thickness"
                          className="w-full bg-void border border-white/10 focus:border-coral rounded-xl px-4 py-3 text-white placeholder:text-gray-600 outline-none" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Cement Grade */}
                {projectType !== 'tiling' && (
                  <div>
                    <label className="font-display text-sm font-medium text-white mb-4 block">Preferred Cement</label>
                    <div className="grid grid-cols-2 gap-3">
                      {cementGrades.map((grade) => (
                        <button key={grade.id} onClick={() => setCementGrade(grade.id)} data-testid={`calc-grade-${grade.id}`}
                          className={`p-3 rounded-xl text-sm font-display transition-all ${cementGrade === grade.id ? 'bg-avi-gold/20 text-avi-gold border border-avi-gold/30' : 'bg-void border border-white/10 text-gray-400 hover:border-white/20'}`}>
                          {grade.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-4">
                  <button onClick={calculate} data-testid="calc-calculate-btn"
                    className="flex-1 flex items-center justify-center gap-2 bg-coral hover:bg-coral-dark text-white font-display font-medium py-4 rounded-full transition-all">
                    <Calculator className="w-5 h-5" /> Calculate
                  </button>
                  <button onClick={reset} data-testid="calc-reset-btn"
                    className="flex items-center justify-center gap-2 border border-white/20 hover:border-white/40 text-gray-300 hover:text-white font-display px-6 py-4 rounded-full transition-all">
                    <RotateCcw className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Results Panel */}
            <div className="lg:col-span-2">
              <div className="bg-surface border border-white/5 rounded-2xl p-6 md:p-8 sticky top-28">
                <h3 className="font-display text-lg font-semibold text-white mb-6">Estimated Materials</h3>
                {results ? (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="calc-results">
                    <p className="text-gray-500 text-xs font-mono mb-4">{results.description}</p>
                    {results.cementBags > 0 && <ResultRow label="Cement Bags" value={results.cementBags} unit="bags (50kg)" color="#E85A1C" />}
                    {results.sand > 0 && <ResultRow label="Sand" value={results.sand} unit="cu.m" color="#F5A623" />}
                    {results.aggregate > 0 && <ResultRow label="Aggregate" value={results.aggregate} unit="cu.m" color="#D4A418" />}
                    {results.blocks > 0 && <ResultRow label="AAC Blocks" value={results.blocks} unit="blocks" color="#0288D1" />}
                    {results.tileAdhesive > 0 && <ResultRow label="Tile Adhesive" value={results.tileAdhesive} unit="kg" color="#5E35B1" />}
                    {results.plaster > 0 && <ResultRow label="Ready Mix Plaster" value={results.plaster} unit="kg" color="#388E3C" />}
                    <div className="border-t border-white/5 pt-4 mt-6">
                      <p className="text-gray-600 text-xs mb-4">* Estimates are approximate. Actual quantities may vary based on site conditions, wastage, and mix design.</p>
                      <Link to="/contact" data-testid="calc-cta-quote"
                        className="flex items-center justify-center gap-2 bg-coral/10 hover:bg-coral/20 text-coral font-display text-sm font-medium py-3 rounded-full transition-all w-full">
                        Get Exact Quote <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </motion.div>
                ) : (
                  <div className="text-center py-12" data-testid="calc-empty-state">
                    <Calculator className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                    <p className="text-gray-500 text-sm">Enter your project dimensions and click Calculate to see estimated materials.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const ResultRow = ({ label, value, unit, color }) => (
  <div className="flex items-center justify-between bg-void/50 border border-white/5 rounded-xl p-4">
    <div className="flex items-center gap-3">
      <div className="w-3 h-3 rounded" style={{ backgroundColor: color }} />
      <span className="text-gray-300 text-sm">{label}</span>
    </div>
    <div className="text-right">
      <span className="text-white font-display font-semibold">{value}</span>
      <span className="text-gray-500 text-xs ml-1">{unit}</span>
    </div>
  </div>
);

export default CalculatorPage;
